import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Panel, PanelHeading } from '../components/ui/panel';
import { Eye, EyeSlash, Warning } from '@phosphor-icons/react';
import StaffEnrollment from '../components/StaffEnrollment';
import Gate from '../components/access/Gate';
import { loginErrorMessage, accessErrorMessage } from '../lib/accessCopy';

const ErrorLine = ({ children }) => (
  <div
    role="alert"
    className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/25 rounded-lg p-3"
    data-testid="error-message"
  >
    <Warning className="w-4 h-4 mt-0.5 shrink-0" weight="fill" aria-hidden="true" />
    <span>{children}</span>
  </div>
);

// Panel, footer and padding, measured in the browser at 360px wide. Lets the
// gate art shrink so the form fits under the crest where the screen allows.
// Where it does not (a narrow phone with the browser's own bars showing), the
// art cannot shrink further without leaving the width uncovered, so the page
// scrolls a little -- with Enter Arcanum still inside the first screenful.
const LOGIN_RESERVE = 450;

const Login = () => {
  const { login, verifyTwoFactor } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState('credentials'); // credentials | twofa | enroll
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // "/" sends each role to its own landing: Room for members.
      navigate('/', { replace: true });
    } else if (result.twoFactorRequired) {
      setStage('twofa');
    } else if (result.enrollmentRequired) {
      setStage('enroll');
    } else {
      // The address stays so a retry is one field, not two.
      setError(loginErrorMessage(result));
    }

    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await verifyTwoFactor(code.trim());
    if (result.success) {
      navigate('/', { replace: true });
    } else {
      setError(accessErrorMessage(result, 'That code was not accepted.'));
    }
    setLoading(false);
  };

  return (
    <Gate
      data-testid="login-screen"
      reserve={LOGIN_RESERVE}
      footer={
        <>
          <p>No public signup &middot; Admin-controlled access only</p>
          {/* The disclosure stays where every member reads it first. */}
          <p className="mt-1">
            This system uses server-side access control. Messages are not
            end-to-end encrypted.
          </p>
        </>
      }
    >
      <Panel className="px-5 pt-4 pb-5">
        {stage === 'enroll' ? (
          <StaffEnrollment
            onDone={() => navigate('/dashboard')}
            onCancel={() => { setStage('credentials'); setError(''); }}
          />
        ) : stage === 'twofa' ? (
          <form onSubmit={handleVerify} className="space-y-4" data-testid="twofa-form">
            <div className="text-center">
              <PanelHeading>Two-factor authentication</PanelHeading>
              <p className="text-sm text-muted-foreground mt-1">
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="code" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Code
              </Label>
              <Input
                id="code"
                inputMode="text"
                autoComplete="one-time-code"
                placeholder="Enter your code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                className="h-11 text-center tracking-[0.3em] placeholder:tracking-normal placeholder:text-sm"
                data-testid="twofa-code-input"
              />
            </div>
            {error && <ErrorLine>{error}</ErrorLine>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold tracking-wide"
              data-testid="twofa-verify-button"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </Button>
            <button
              type="button"
              onClick={() => { setStage('credentials'); setCode(''); setError(''); }}
              className="w-full min-h-[44px] text-xs text-muted-foreground hover:text-foreground"
            >
              Back to sign in
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="login-form">
            <div className="text-center pb-2.5 border-b border-border">
              <PanelHeading>Member Access</PanelHeading>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-12 h-11"
                  data-testid="password-input"
                />
                {/* A password you cannot read is a password you mistype, and
                    the only feedback is being told your credentials are wrong.
                    tabIndex -1 so it does not sit between the field and the
                    sign-in button. */}
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-0 top-0 h-11 w-11 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && <ErrorLine>{error}</ErrorLine>}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold tracking-wide"
              data-testid="login-button"
            >
              {loading ? 'Entering…' : 'Enter Arcanum'}
            </Button>

            {/* Not a dead end. There is no e-mail reset here and there never
                will be, so the honest thing is to say what the way back
                actually is rather than leave a member staring at the form. */}
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              aria-expanded={showHelp}
              className="w-full min-h-[40px] -mb-3 -mt-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              data-testid="forgot-password"
            >
              Forgotten your password?
            </button>
          </form>
        )}

        {showHelp && stage === 'credentials' && (
          <div
            className="mt-3 p-4 bg-muted/60 border border-border rounded-lg space-y-2.5"
            data-testid="forgot-password-help"
          >
            <p className="text-xs text-muted-foreground leading-relaxed">
              Arcanum sends no e-mail, so there is no reset link to request.
              That is deliberate: your address is the one thing stored here
              that identifies you, and it is not handed to a mail provider.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Ask the Founder directly, the same way you were admitted. You
              will be given a single-use link to set a new password &mdash;
              and the Founder never sees what you choose.
            </p>
            <p className="text-xs leading-relaxed" style={{ color: 'hsl(var(--gold))' }}>
              Your vault is separate. A new password does not open it; that
              needs your vault passphrase, or the recovery key you wrote down.
            </p>
          </div>
        )}
      </Panel>
    </Gate>
  );
};

export default Login;
